import axios from "axios";

// Token storage. In local/standalone, the SPA POSTs to either
// /api/v1/auth/login (real bcrypt password) or /api/v1/dev/login
// (platform-security shortcut, no password) and stores the resulting
// HS256 JWT in localStorage. The request interceptor below attaches it
// as a Bearer header. When datumcraft.security.enabled=false the server
// ignores the header anyway, so the same code path works whether the
// gate is on or off.
const TOKEN_KEY = "bridge.auth.token";

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

// Two axios instances. `api` is /api/v1/bridge for product calls; `root`
// is /api/v1 for license + auth endpoints that live outside the bridge
// namespace (provided by platform-licensing + bridge-api/security).
export const api = axios.create({
  baseURL: "/api/v1/bridge",
  withCredentials: true,
});

export const root = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
});

function attachAuth(config: import("axios").InternalAxiosRequestConfig) {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}

api.interceptors.request.use(attachAuth);
root.interceptors.request.use(attachAuth);

// 401 from any backend call clears the token and bounces to /login.
// Skips the redirect when we're already on /login so the LoginPage's
// own 401 handling (showing "invalid credentials") still runs.
function on401(err: unknown): Promise<never> {
  // axios shape — narrow without dragging the full type in.
  const e = err as { response?: { status?: number } };
  if (e?.response?.status === 401) {
    clearAuthToken();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
  }
  return Promise.reject(err);
}

api.interceptors.response.use((r) => r, on401);
root.interceptors.response.use((r) => r, on401);

// ----- Domain types — mirror Java records in bridge-core -----

export type AssetId = { platform: string; value: string };

export type SourcePlatform = "COGNOS" | "POWERBI" | "TABLEAU";
export type TargetPlatform = "POWERBI_PREMIUM" | "TABLEAU_CLOUD" | "QLIK_SENSE" | "LOOKER";

export type DataType =
  | "STRING" | "INTEGER" | "DECIMAL" | "BOOLEAN"
  | "DATE" | "TIMESTAMP" | "UNKNOWN";

export type IrField = {
  name: string;
  dataType: DataType;
  expression?: string | null;
  description?: string | null;
};

export type IrFilter = {
  label: string;
  sourceExpression: string;
  description?: string | null;
};

export type VisualKind =
  | "TABLE" | "LIST" | "BAR_CHART" | "COLUMN_CHART"
  | "LINE_CHART" | "PIE_CHART" | "SCATTER" | "MAP"
  | "KPI" | "TEXT_BLOCK" | "IMAGE" | "UNKNOWN";

export type IrVisualization = {
  id: string;
  kind: VisualKind;
  title?: string | null;
  fieldNames: string[];
};

export type CognosReportIR = {
  sourceAssetId: AssetId;
  sourcePlatform: SourcePlatform;
  displayName: string;
  packagePath?: string | null;
  fields: IrField[];
  filters: IrFilter[];
  visualizations: IrVisualization[];
};

export type UnmappedConcept = {
  category: string;
  sourceRef: string;
  reason: string;
  fixHint: string;
};

export type TranslationResult = {
  sourceAssetId: AssetId;
  target: TargetPlatform;
  translatedAt: string;
  confidence: number;
  artifactName: string;
  artifactMimeType: string;
  artifactJson: string;
  unmappedConcepts: UnmappedConcept[];
  notes: string[];
};

export type ValidationSeverity = "ERROR" | "WARNING" | "INFO";

export type ValidationFinding = {
  severity: ValidationSeverity;
  code: string;
  message: string;
  location: string;
};

export type ValidationReport = {
  sourceAssetId: AssetId;
  target: TargetPlatform;
  generatedAt: string;
  findings: ValidationFinding[];
  countsBySeverity: Partial<Record<ValidationSeverity, number>>;
};

// ----- Auth + license types -----

export type LoginResponse = {
  token: string;
  user: string;
  displayName?: string;
  roles: string[];
  expiresInSeconds: number;
};

export type LicenseStatus = {
  ok: boolean;
  failureReason?: string | null;
  failureMessage?: string | null;
  customerName?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  daysRemaining: number;
  fingerprint: string;
};

// ----- Job tracking types -----

export type JobKind = "PARSE" | "TRANSLATE" | "VALIDATE";
export type JobStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED";

export type BridgeJob = {
  id: string;
  kind: JobKind;
  status: JobStatus;
  submittedAt: string;
  completedAt?: string | null;
  submittedBy?: string | null;
  target?: TargetPlatform | null;
  ir?: CognosReportIR | null;
  translation?: TranslationResult | null;
  validation?: ValidationReport | null;
  errorMessage?: string | null;
};

// ----- Config + translator registry types -----

export type ConfigResponse = {
  llmProvider: string;
  productVersion: string;
  translatorCount: number;
  featureFlags: Record<string, boolean>;
};

export type TranslatorDescriptor = {
  id: string;
  className: string;
  supportedTargets: string[];
};

export async function fetchConfig(): Promise<ConfigResponse> {
  const r = await root.get<ConfigResponse>("/config");
  return r.data;
}

export async function fetchTranslators(): Promise<TranslatorDescriptor[]> {
  const r = await root.get<TranslatorDescriptor[]>("/bridge/translators");
  return r.data;
}

// ----- API methods -----

export type CognosSpec = {
  found: boolean;
  source?: "soap" | "empty";
  contentType?: "application/xml" | "application/json" | "text/plain";
  sizeBytes?: number;
  xml?: string;
  message?: string;
  path?: string;
  storeId?: string;
};

/**
 * Fetch the spec / model XML for a Cognos object by search-path.
 * Backed by the platform's shared {@code /api/v1/content/spec}
 * endpoint (in {@code surface-rest}). Returns {@code found:false}
 * with a {@code message} when the SDK is disabled or the picked
 * object is a folder; the UI uses that to keep the "Load" button in
 * a sane state without surfacing 4xx in the browser console.
 */
export async function fetchCognosSpec(searchPath: string): Promise<CognosSpec> {
  const r = await root.get<CognosSpec>("/content/spec", { params: { path: searchPath } });
  return r.data;
}

export async function parseReport(assetId: AssetId, rawSource: string): Promise<CognosReportIR> {
  const r = await api.post<CognosReportIR>("/parse", { assetId, rawSource });
  return r.data;
}

export async function parseReportUpload(
  file: File,
  platform: string,
  assetId?: string,
): Promise<CognosReportIR> {
  const form = new FormData();
  form.append("file", file);
  form.append("platform", platform);
  if (assetId) form.append("assetId", assetId);
  const r = await api.post<CognosReportIR>("/parse/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

export async function translateReport(
  report: CognosReportIR,
  target: TargetPlatform,
): Promise<TranslationResult> {
  const r = await api.post<TranslationResult>("/translate", { report, target });
  return r.data;
}

export async function validateTranslation(
  source: CognosReportIR,
  translation: TranslationResult | null,
): Promise<ValidationReport> {
  const r = await api.post<ValidationReport>("/validate", { source, translation });
  return r.data;
}

export async function recentJobs(limit = 50): Promise<BridgeJob[]> {
  const r = await api.get<BridgeJob[]>("/jobs/recent", { params: { limit } });
  return r.data;
}

export async function fetchJob(id: string): Promise<BridgeJob> {
  const r = await api.get<BridgeJob>(`/jobs/${id}`);
  return r.data;
}

export async function fetchLicenseStatus(): Promise<LicenseStatus> {
  const r = await root.get<LicenseStatus>("/license/status");
  return r.data;
}
